'use client'

import { Icon } from '@components/primitives/Icon'
import { getColorConfig } from '@components/primitives/ToggleButton/getColorConfig'
import { classMerge } from '@components/utilities/classMerge'
import type { IconDefinition } from '@fortawesome/fontawesome-common-types'
import { useState } from 'react'

export const RadioSingleSelect = ({
  autoSubmitFormOnChoice = false,
  centerAlignOptions = true,
  defaultValue,
  formRef,
  name,
  onChange,
  options,
  required = false,
}: {
  autoSubmitFormOnChoice?: boolean
  centerAlignOptions?: boolean
  defaultValue?: string
  formRef?: React.MutableRefObject<HTMLFormElement | null>
  name: string
  onChange: (value: string) => void
  options: Array<{ icon?: IconDefinition; label: string; value: string }>
  required?: boolean
}) => {
  const [selected, setSelected] = useState(defaultValue)

  const handleChange = (value: string) => {
    setSelected(value)
    setTimeout(() => {
      formRef?.current?.dispatchEvent(new Event('input'))

      if (autoSubmitFormOnChoice) {
        formRef?.current?.requestSubmit()
      }

      onChange(value) // not sure what is going on here - we need a second for it to realize?
    })
  }

  const {
    backgroundColor,
    selectedBorderColor,
    selectedTextColor,
    unselectedTextColor,
  } = getColorConfig('success')

  return (
    <>
      {options.map(({ icon, label, value }) => (
        <div
          className={classMerge(
            'cursor-pointer rounded-lg border-2 border-neutral200 p-4 hover:bg-neutral100',
            'flex items-center',
            selected === value && `${selectedBorderColor} ${backgroundColor}`,
            selected === value ? selectedTextColor : unselectedTextColor,
            centerAlignOptions ? 'justify-center' : 'justify-start',
          )}
          key={value}
          onClick={() => handleChange(value)}
          onKeyDown={(e) => {
            // select with spacebar
            if (e.key === ' ') {
              handleChange(value)
            }
          }}
          tabIndex={0}
        >
          <input
            checked={selected === value}
            className='opacity-0'
            id={value}
            name={name}
            onChange={() => {}} // handled by parent, noop to avoid react warning
            required={required}
            tabIndex={-1}
            type='radio'
            value={value}
          />
          <label
            className='-ml-3 cursor-pointer text-sm font-bold' // -ml-3 is to work around the hidden radio circle
            htmlFor={value}
          >
            {icon && <Icon className='mr-3' icon={icon} />}
            {label}
          </label>
        </div>
      ))}
    </>
  )
}
